import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Avatar, Box, Card, CardActionArea, CardContent, IconButton, Tooltip, Typography } from "@mui/material";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { alpha } from "@mui/material/styles";
import placeholderProjectCardPng from "@/assets/placeholder-project-card.png";
import { formatDate } from "@/utils/utils";
import { initialsFromEmail } from "@/utils/user";
import { useProjectListItemPermissions } from "@/hooks/useProjectPermissions";

export interface ProjectCardData {
  readonly id: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly editedTimestamp?: number;
  readonly thumbnailUrl?: string;
  readonly isArchived?: boolean;
  /** Owner's email - drives the lock/access badge and the owner-initials avatar. */
  readonly owner?: string | null;
  readonly isLocked?: boolean;
  readonly lockedBy?: string | null;
  /** Sort-only fields, not rendered by this card. */
  readonly sortCreatedAt?: number;
  readonly sortModifiedAt?: number;
}

interface ProjectCardProps {
  readonly project: ProjectCardData;
  readonly onOpen: (id: string) => void;
  readonly onContextMenu: (event: React.MouseEvent, id: string) => void;
}

const hoverActionsSelector = "&:hover [data-project-card-actions], &:focus-within [data-project-card-actions]" as const;

const ProjectCard = ({ project, onOpen, onContextMenu }: ProjectCardProps) => {
  const { isOwner, isLocked } = useProjectListItemPermissions({
    owner: project.owner,
    is_locked: project.isLocked,
    locked_by: project.lockedBy
  });

  let accessIcon: ReactNode;
  let accessTooltip: string;
  if (isOwner) {
    accessIcon = isLocked ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenOutlinedIcon sx={{ fontSize: 16 }} />;
    accessTooltip = isLocked ? "Locked" : "Unlocked";
  } else {
    accessIcon = isLocked ? <VisibilityOutlinedIcon sx={{ fontSize: 16 }} /> : <EditOutlinedIcon sx={{ fontSize: 16 }} />;
    accessTooltip = isLocked ? "View only" : "Edit access";
  }

  const ownerInitials = initialsFromEmail(project.owner);

  const titleRef = useRef<HTMLSpanElement | null>(null);
  const [isTitleOverflowing, setIsTitleOverflowing] = useState(false);
  const [isTitleHovered, setIsTitleHovered] = useState(false);

  const checkTitleOverflow = useCallback(() => {
    const el = titleRef.current;
    if (!el) {
      return;
    }

    // Use ceil to avoid fractional-width false negatives.
    setIsTitleOverflowing(Math.ceil(el.scrollWidth) > Math.ceil(el.clientWidth));
  }, []);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) {
      return;
    }

    checkTitleOverflow();
    globalThis.requestAnimationFrame(() => checkTitleOverflow());

    const ro = new ResizeObserver(() => checkTitleOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkTitleOverflow, project.title]);

  return (
    <Card
      data-testid="project-card"
      data-project-id={project.id}
      elevation={0}
      sx={{
        borderRadius: 2,
        bgcolor: (t) => alpha(t.palette.common.white, 0.06),
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider"
      }}
    >
      <CardActionArea
        component="div"
        role="button"
        tabIndex={0}
        onClick={() => onOpen(project.id)}
        onContextMenu={(e) => onContextMenu(e, project.id)}
        sx={{
          display: "block",
          [hoverActionsSelector]: {
            opacity: 1
          }
        }}
      >
        <Box
          sx={(theme) => ({
            position: "relative",
            height: 140,
            bgcolor: alpha(theme.palette.common.white, 0.06),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundImage: `url(${project.thumbnailUrl ?? placeholderProjectCardPng})`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          })}
        >
          <Tooltip title={accessTooltip} arrow enterDelay={300}>
            <Box
              sx={(theme) => ({
                position: "absolute",
                top: 12,
                left: 12,
                width: 26,
                height: 26,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.common.white,
                bgcolor: alpha(theme.palette.common.black, 0.55)
              })}
            >
              {accessIcon}
            </Box>
          </Tooltip>
          <Box
            data-project-card-actions
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              right: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              opacity: 0,
              pointerEvents: "none",
              transition: (t) => t.transitions.create("opacity", { duration: t.transitions.duration.shortest })
            }}
          >
            <IconButton
              size="small"
              sx={(theme) => ({
                pointerEvents: "auto",
                color: theme.palette.common.white,
                bgcolor: alpha(theme.palette.common.black, 0.5),
                "&:hover": { bgcolor: alpha(theme.palette.common.black, 0.7) }
              })}
              aria-label="Project actions"
              onClick={(e) => {
                e.stopPropagation();
                onContextMenu(e, project.id);
              }}
              onContextMenu={(e) => {
                e.stopPropagation();
                onContextMenu(e, project.id);
              }}
            >
              <MoreVertOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <CardContent sx={{ p: 2, bgcolor: "background.default" }}>
          <Tooltip
            title={project.title}
            open={isTitleHovered ? isTitleOverflowing : false}
            placement="top-start"
            disableHoverListener
            disableFocusListener
            disableTouchListener
          >
            <Typography
              ref={titleRef}
              component="span"
              variant="subtitle2"
              noWrap
              onMouseEnter={() => {
                checkTitleOverflow();
                setIsTitleHovered(true);
              }}
              onMouseLeave={() => setIsTitleHovered(false)}
              sx={{ color: "text.primary", display: "block", maxWidth: "100%" }}
            >
              {project.title}
            </Typography>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              minHeight: 18
            }}
          >
            {project.subtitle ?? "\u00A0"}
          </Typography>
          <Box sx={{ pt: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {project.editedTimestamp ? `Edited: ${formatDate(project.editedTimestamp)}` : "\u00A0"}
            </Typography>
            {ownerInitials ? (
              <Tooltip title={project.owner ?? ""} arrow>
                <Avatar sx={{ width: 24, height: 24, fontSize: 11, fontWeight: 600 }}>{ownerInitials}</Avatar>
              </Tooltip>
            ) : null}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default ProjectCard;
